import Money from 'common/Money';
import {displayFractionDigits} from 'src/lib/currencyUnits';
import type {FullUnitFragment} from '@transactions/graphql';

/**
 * Custom-unit-aware display helpers.
 *
 * A `currency` string is either an ISO 4217-style code (self-describing,
 * rendered through Intl by Money.toDecimalString) or the id of a custom Unit
 * (an opaque `unit_...` token whose display content — name, singular/plural
 * labels, decimals — lives on the Unit document). These helpers take the
 * units map (from useUnitsById) and route each currency to the right
 * renderer, so amounts read "3 passes" instead of "3.00 unit_68f...".
 *
 * Pure functions over an explicit map rather than hooks, so they compose
 * into non-component code (chart tick formatters, sort keys); the hooks in
 * src/hooks/useUnits.ts wrap them for component use.
 */

export type UnitsById = ReadonlyMap<string, FullUnitFragment>;

/**
 * Whether a currency string is (shaped like) a custom-unit token. Tokens are
 * minted with this prefix by both the server and the in-memory test storage,
 * and legacy free-text codes can never collide with it — the old validation
 * pattern required uppercase.
 */
export const isUnitToken = (currency: string): boolean =>
  currency.startsWith('unit_');

const clampDecimals = (decimals: number): number =>
  Math.min(2, Math.max(0, decimals));

/**
 * Fraction digits a currency displays AND parses: the unit's configured
 * displayDecimals for custom units, Intl's answer (capped at 2) otherwise.
 * Display and entry must agree so rendered amounts round-trip through
 * amount inputs.
 */
export const fractionDigitsWithUnits = (
  currency: string,
  unitsById: UnitsById,
): number => {
  const unit = unitsById.get(currency);
  return unit ? clampDecimals(unit.displayDecimals) : displayFractionDigits(currency);
};

/**
 * The smallest DISPLAYABLE amount in stored hundredths (unit-aware analog of
 * lib/currencyUnits.displayUnitHundredths): 1 for USD cents, 100 for a
 * whole-pass zero-decimal unit. Generated amounts (even splits) should be
 * multiples of this so they render exactly and survive re-editing.
 */
export const displayUnitHundredthsWithUnits = (
  currency: string,
  unitsById: UnitsById,
): number => 10 ** (2 - fractionDigitsWithUnits(currency, unitsById));

type RenderOptions = {
  showCurrency?: boolean;
};

/**
 * Render a Money: custom units as "3 passes" / "1 pass" (label picked by
 * magnitude, decimals per the unit), everything else via
 * Money.toDecimalString. With showCurrency: false, the bare number at the
 * unit's decimals — the shape amount inputs parse.
 */
export const formatMoneyWithUnits = (
  money: Money,
  unitsById: UnitsById,
  options?: RenderOptions,
): string => {
  const unit = unitsById.get(money.currency);
  if (!unit) {
    return Money.toDecimalString(money, options);
  }
  const digits = clampDecimals(unit.displayDecimals);
  const value = money.amount / 100;
  const bare = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
  if (options?.showCurrency === false) {
    return bare;
  }
  const label = Math.abs(value) === 1 ? unit.singular : unit.plural;
  return `${bare} ${label}`;
};

/**
 * Human label for a currency slot: the unit's full name for custom units
 * (e.g. "Court 16 drills passes"), the code verbatim otherwise. For places
 * that print `account.currency` as a label, which would otherwise show the
 * raw token.
 */
export const currencyLabelWithUnits = (
  currency: string,
  unitsById: UnitsById,
): string => unitsById.get(currency)?.name ?? currency;
